import React, { useContext, useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  LinearProgress
} from "@material-ui/core";
import DescriptionIcon from "@material-ui/icons/Description";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PeopleIcon from "@material-ui/icons/People";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import officialCampaignService from "../../services/officialCampaignService";
import { AuthContext } from "../../context/Auth/AuthContext";
import OfficialCampaignModal from "../../components/OfficialCampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import OfficialContactListsPanel from "../../components/OfficialContactListsPanel";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  statusChip: {
    textTransform: "uppercase",
    fontWeight: 600
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  progressCell: {
    minWidth: 160
  },
  tableHeader: {
    fontWeight: "bold"
  }
}));

const asText = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return fallback;
};

const asArray = (value, keys = ["records", "data"]) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value || typeof value !== "object") return [];
  for (const key of keys) {
    if (Array.isArray(value[key])) return value[key].filter(Boolean);
  }
  return [];
};

const OfficialCampaigns = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [campaigns, setCampaigns] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(history.location.search).get("tab") === "contacts" ? 1 : 0
  );

  const companyId = user?.companyId;
  const canUseOfficialCampaign = !!user && (user.profile !== "user" || user.allowOfficialCampaign === "enabled");

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await officialCampaignService.list({
        pageSize: 50,
        status: statusFilter || undefined,
        searchParam: searchParam || undefined
      });
      setCampaigns(asArray(data).filter(campaign => campaign?.id));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchParam]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    const handler = payload => {
      if (!payload) return;
      setCampaigns(prev => {
        const list = [...prev];
        if (payload.action === "delete") {
          return list.filter(item => item.id !== Number(payload.id));
        }
        if (!payload.record?.id) {
          return list;
        }
        const index = list.findIndex(item => item.id === payload.record.id);
        if (index >= 0) {
          list[index] = payload.record;
          return [...list];
        }
        return [payload.record, ...list];
      });
    };

    socket?.on(`company-${companyId}-official-campaign`, handler);
    return () => socket?.off(`company-${companyId}-official-campaign`, handler);
  }, [socket, companyId]);

  const handleSearch = event => {
    setSearchParam(String(event?.target?.value || "").toLowerCase());
  };

  const formatStatus = (val) => {
    const status = asText(val, "");
    switch (status) {
      case "scheduled":
        return i18n.t("campaignReport.scheduled");
      case "running":
        return i18n.t("campaignReport.process");
      case "finished":
        return i18n.t("campaignReport.finished");
      case "paused":
        return "Pausada";
      case "draft":
        return "Rascunho";
      case "cancelled":
        return i18n.t("campaignReport.cancelled");
      default:
        return status || "—";
    }
  };

  const getStatusColor = status => {
    switch (asText(status, "")) {
      case "running":
        return "primary";
      case "finished":
        return "default";
      case "scheduled":
        return "secondary";
      case "paused":
        return "default";
      case "draft":
        return "default";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const handleOpenModal = () => {
    setEditingCampaignId(null);
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCampaignId(null);
  };

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    history.replace(value === 1 ? "/official-campaigns?tab=contacts" : "/official-campaigns");
  };

  const cancelCampaign = async (campaign) => {
    try {
      await officialCampaignService.cancel(campaign.id);
      toast.success("Campanha pausada com sucesso!");
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await officialCampaignService.restart(campaign.id);
      toast.success("Campanha reiniciada com sucesso!");
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await officialCampaignService.remove(campaignId);
      toast.success("Campanha excluída com sucesso!");
      setDeletingCampaign(null);
      setConfirmModalOpen(false);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    !canUseOfficialCampaign ? (
      <ForbiddenPage />
    ) : (
      <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={8} item>
            <Title>Campanhas Oficiais</Title>
          </Grid>
          {activeTab === 0 && <Grid xs={12} sm={4} item>
            <Grid spacing={2} container>
              <Grid xs={6} sm={6} item>
                <TextField
                  fullWidth
                  placeholder="Pesquisa"
                  type="search"
                  value={searchParam}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={6} sm={6} item>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOpenModal}
                  color="primary"
                >
                  Nova Campanha
                </Button>
              </Grid>
            </Grid>
          </Grid>}
        </Grid>
      </MainHeader>

      <Paper style={{ marginBottom: 16 }}>
        <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="fullWidth">
          <Tab icon={<DescriptionIcon />} label="Campanhas" />
          <Tab icon={<PeopleIcon />} label="Listas de contatos Meta" />
        </Tabs>
      </Paper>

      {activeTab === 0 && <>
      {/* Filtros */}
      <Paper className={classes.paper} style={{ padding: 16, marginBottom: 16 }}>
        <Grid spacing={2} container>
          <Grid xs={12} sm={4} item>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Filtrar por Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={event => setStatusFilter(event.target.value)}
                label="Filtrar por Status"
              >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="scheduled">{i18n.t("campaignReport.scheduled")}</MenuItem>
              <MenuItem value="running">{i18n.t("campaignReport.process")}</MenuItem>
              <MenuItem value="finished">{i18n.t("campaignReport.finished")}</MenuItem>
              <MenuItem value="paused">Pausada</MenuItem>
              <MenuItem value="cancelled">{i18n.t("campaignReport.cancelled")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center" className={classes.tableHeader}>
                Nome
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Conexão
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Contatos
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Status
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Agendamento
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Conclusão
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Progresso
              </TableCell>
              <TableCell align="center" className={classes.tableHeader}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map(campaign => (
              <TableRow key={campaign.id}>
                <TableCell>{asText(campaign.name)}</TableCell>
                <TableCell>{asText(campaign.whatsapp?.name || campaign.whatsappId)}</TableCell>
                <TableCell>{asText(campaign.totalContacts)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={formatStatus(campaign.status)}
                    color={getStatusColor(campaign.status)}
                    className={classes.statusChip}
                  />
                  {campaign.pausedReason && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      Motivo: {campaign.pausedReason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {campaign.scheduledAt
                    ? new Date(campaign.scheduledAt).toLocaleString()
                    : "—"}
                </TableCell>
                <TableCell>
                  {campaign.completedAt
                    ? new Date(campaign.completedAt).toLocaleString()
                    : "—"}
                </TableCell>
                <TableCell className={classes.progressCell}>
                  <LinearProgress variant="determinate" value={(Number(campaign.progress) || 0) * 100} />
                  <Typography variant="caption">
                    {((Number(campaign.progress) || 0) * 100).toFixed(0)}%
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Aceitas: {Number(campaign.sentCount) || 0} | Entregues: {Number(campaign.deliveredCount) || 0} | Falhas: {Number(campaign.failedCount) || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                    {["running", "scheduled"].includes(campaign.status) && (
                      <Tooltip title="Pausar Campanha">
                        <IconButton
                          onClick={() => cancelCampaign(campaign)}
                          size="small"
                        >
                          <PauseCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {["cancelled", "paused"].includes(campaign.status) && (
                      <Tooltip title="Continuar Campanha">
                        <IconButton
                          onClick={() => restartCampaign(campaign)}
                          size="small"
                        >
                          <PlayCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {campaign.status === "draft" && (
                      <Tooltip title="Editar rascunho">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingCampaignId(campaign.id);
                            setModalOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Relatório">
                      <IconButton
                        size="small"
                        onClick={() => history.push(`/official-campaigns/${campaign.id}/report`)}
                      >
                        <DescriptionIcon />
                      </IconButton>
                    </Tooltip>
                    {campaign.status !== "running" && (
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingCampaign(campaign);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!campaigns.length && !loading && (
          <Box py={3} textAlign="center" color="textSecondary">
            Nenhuma campanha encontrada.
          </Box>
        )}
        {loading && (
          <Box py={2}>
            <LinearProgress />
          </Box>
        )}
      </Paper>
      </>}

      {activeTab === 1 && <OfficialContactListsPanel />}

      {modalOpen && (
        <OfficialCampaignModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSuccess={fetchCampaigns}
          campaignId={editingCampaignId}
        />
      )}

      <ConfirmationModal
        title={
          deletingCampaign &&
          `Excluir campanha ${asText(deletingCampaign.name)}?`
        }
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setDeletingCampaign(null);
        }}
        onConfirm={() => handleDeleteCampaign(deletingCampaign?.id)}
      >
        Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.
      </ConfirmationModal>
      </MainContainer>
    )
  );
};

export default OfficialCampaigns;
